/**
 * Générateur — Automatisme : donner l'expression générique d'un nombre pair,
 * d'un nombre impair.
 * Attendu ID : 1232 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 799).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Un fait nu à deux entrées : un nombre pair s'écrit 2n, un nombre impair
 * 2n + 1 (n entier), dans les trois sens : mot → écriture, écriture → mot,
 * nombre → écriture (15 = 2 × 7 + 1). Les preuves par le calcul littéral
 * sont l'objectif de 5e (gen_938), hors périmètre. 2n − 1, 4n, 2n + 2 ne
 * sont jamais servis (ni distracteurs, ni lignes vraies : choix mode auto,
 * À ARBITRER). Aucune activité MathsMentales : variables du programme
 * (mise en œuvre Eduscol 2026 p. 18 « 2n / 2n + 1 »).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte « Écris la forme » (nombre_saisie) : « Écris 48 sous la forme 2n
 *     ou 2n + 1 » en saisie libre, comparaison d'expression (prof 13/09) :
 *     2 × 24 ou 24 × 2 ; 49 : 2 × 24 + 1, 1 + 2 × 24… ; jamais + 0.
 *   Carte 1 « Pair ou impair ? » (ecriture_qcm_unique) : « Un nombre pair
 *     s'écrit… » [2n ; n + 2 ; n² ; 2n + 1] / « Un nombre impair s'écrit… »
 *     [2n + 1 ; n + 1 ; 2n ; n + 2] ; contenu.sousType = pair / impair.
 *   Carte 2 « Le nombre en 2n ou 2n + 1 » (nombre_texte_trou_saisie) :
 *     quatre lignes « 14 = 2 × ⬚ », « 15 = 2 × ⬚ + 1 », n de 3 à 49.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes « 2n + 1 est
 *     toujours impair. » (V), « n + 2 est toujours pair. » (F), « n² est
 *     toujours pair. » (F), « 2n est toujours pair. » (V)… tirées d'un pool
 *     fixe à vérité fixe, re-vérifiée sur n = 0..20 (throw).
 *
 * SACRIFICE : aucun (les QCM sont du pur rappel, règle 9 : pas de fiche sur
 * eux ; la fiche des paires vit sur la carte numérique : n = 7, 14 et 15
 * sacrifiés).
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   ajout_pour_double     : n + 2 pour pair (nom de 1110), PRINCIPALE ;
 *   successeur_pour_impair : n + 1 pour impair ;
 *   sens_inverse          : 2n pour impair, 2n + 1 pour pair ;
 *   carre                 : n² pour pair.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeSaisie, tex,
} from '../../../utils';

const ATTENDU_ID = 1232;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Pair ou impair ?';
const L_NOMBRE = 'Le nombre en 2n ou 2n + 1';
const L_VF = 'Vrai ou faux';
const L_FORME = 'Écris la forme';

/** Le n de la fiche (14 = 2 × 7, 15 = 2 × 7 + 1). */
export const N_SACRIFIE = 7;

const EXPLICATIONS = {
    ajout_pour_double: 'Un nombre pair, c\'est deux fois un entier : 2n. n + 2 n\'est pair que si n l\'est.',
    successeur_pour_impair: 'n + 1 est pair ou impair selon n. Un impair, c\'est un pair plus 1 : 2n + 1.',
    sens_inverse: '2n est pair (14 = 2 × 7), 2n + 1 est impair (15 = 2 × 7 + 1).',
    carre: 'n² est pair ou impair selon n : ce n\'est pas l\'écriture d\'un pair.',
    regle: 'Pair : 2n. Impair : 2n + 1. Pour un nombre donné, je cherche le double le plus proche en dessous : 15 = 2 × 7 + 1.',
};

// ─── Carte 1 : QCM ────────────────────────────────────────────────────────

const LETTRES = ['n', 'n', 'p', 'k', 'm'];

/** Deux faits nus, mais des variantes : la lettre, l'amorce, trois distracteurs tirés parmi quatre. */
const genQcm = () => {
    const pair = randomChoice([true, false]);
    const L = randomChoice(LETTRES);
    const bon = tex(pair ? `2${L}` : `2${L} + 1`);
    const pool = pair
        ? [{ code: 'ajout_pour_double', texte: tex(`${L} + 2`) }, { code: 'carre', texte: tex(`${L}^{2}`) }, { code: 'sens_inverse', texte: tex(`2${L} + 1`) }, { code: 'successeur_pour_impair', texte: tex(`${L} + 1`) }]
        : [{ code: 'successeur_pour_impair', texte: tex(`${L} + 1`) }, { code: 'sens_inverse', texte: tex(`2${L}`) }, { code: 'ajout_pour_double', texte: tex(`${L} + 2`) }, { code: 'carre', texte: tex(`${L}^{2}`) }];
    const faux = shuffle(pool).slice(0, 3);
    const mot = pair ? 'pair' : 'impair';
    const stem = randomChoice([
        `<p><b>Un nombre ${mot} s'écrit… (${tex(L)} entier)</b></p>`,
        `<p><b>Laquelle de ces écritures désigne toujours un nombre ${mot} ? (${tex(L)} entier)</b></p>`,
    ]);
    const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, stem, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: mot, explications: EXPLICATIONS };
    payload._meta.params = { pair, lettre: L, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : le nombre en 2n ou 2n + 1 (saisie) ─────────────────────────

const CONSIGNE_NOMBRE = '<p><b>Écris chaque nombre sous la forme</b> ' + tex('2n') + ' <b>ou</b> ' + tex('2n + 1') + ' (' + tex('n') + ' entier). <b>Complète.</b></p>';

const genNombre = () => {
    for (let essai = 0; essai < 100; essai++) {
        const ns = [];
        while (ns.length < 4) { const n = randInt(3, 49); if (n !== N_SACRIFIE && !ns.includes(n)) ns.push(n); }
        const pairs = shuffle([true, true, false, false]);
        // la forme 2n ou 2n + 1 est demandée par la consigne ; un pair s'écrit « 2 × ⬚ », jamais « + 0 » (retour prof 13/09)
        const lignes = ns.map((n, i) => (pairs[i] ? `$${2 * n} = 2 \\times {{${n}}}$` : `$${2 * n + 1} = 2 \\times {{${n}}} + 1$`));
        ns.forEach((n, i) => { if ((pairs[i] ? 2 * n : 2 * n + 1) % 2 !== (pairs[i] ? 0 : 1)) throw new Error('gen_1232 : parité'); });
        const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_NOMBRE, { ...opts(L_NOMBRE), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, sens_inverse: EXPLICATIONS.sens_inverse } };
        payload._meta.params = { ns, pairs };
        return payload;
    }
    const payload = makeTexteTrou(['$24 = 2 \\times {{12}}$', '$31 = 2 \\times {{15}} + 1$', '$46 = 2 \\times {{23}}$', '$9 = 2 \\times {{4}} + 1$'].join('\n'), CONSIGNE_NOMBRE, { ...opts(L_NOMBRE), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { ns: [12, 15, 23, 4], pairs: [true, false, true, false] };
    return payload;
};

// ─── Carte « Écris la forme » (saisie libre, comparaison d'expression) ────

/**
 * Décision prof 13/09 : en plus de la carte à trous, l'élève écrit lui-même
 * la forme : « Écris 48 sous la forme 2n ou 2n + 1 » → 2 × 24 (ou 24 × 2,
 * × et * confondus par le juge) ; 49 → 2 × 24 + 1, 1 + 2 × 24, 24 × 2 + 1,
 * 1 + 24 × 2. Jamais « + 0 ». Le corrigé affiche 2 × 24 (+ 1).
 */
export const ecrituresForme = (N) => {
    const n = Math.floor(N / 2);
    if (N % 2 === 0) return [`2 \u00d7 ${n}`, `${n} \u00d7 2`];
    return [`2 \u00d7 ${n} + 1`, `1 + 2 \u00d7 ${n}`, `${n} \u00d7 2 + 1`, `1 + ${n} \u00d7 2`];
};

const genForme = () => {
    let n = randInt(3, 49);
    while (n === N_SACRIFIE) n = randInt(3, 49);
    const pair = randomChoice([true, false]);
    const N = pair ? 2 * n : 2 * n + 1;
    if (N % 2 !== (pair ? 0 : 1)) throw new Error('gen_1232 : parité');
    const ecritures = ecrituresForme(N);
    const payload = makeSaisie(ecritures[0], `<p><b>Écris ${tex(String(N))} sous la forme</b> ${tex('2n')} <b>ou</b> ${tex('2n + 1')} (${tex('n')} entier).</p>`, {
        ...opts(L_FORME), strict: true, comparaison: 'expression', reponses_acceptees: ecritures,
    });
    payload.contenu = { ...payload.contenu, sousType: pair ? 'pair' : 'impair', explications: { regle: EXPLICATIONS.regle, sens_inverse: EXPLICATIONS.sens_inverse } };
    payload._meta.params = { N, n, pair };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** Le pool : chaque affirmation avec sa vérité, contrôlée sur n = 0..20 par la fonction. */
const POOL = [
    { texte: 'est toujours pair', expr: '2n', f: (n) => 2 * n, parite: 0, vraie: true, code: null },
    { texte: 'est toujours impair', expr: '2n + 1', f: (n) => 2 * n + 1, parite: 1, vraie: true, code: null },
    { texte: 'est toujours pair', expr: 'n + 2', f: (n) => n + 2, parite: 0, vraie: false, code: 'ajout_pour_double' },
    { texte: 'est toujours impair', expr: 'n + 1', f: (n) => n + 1, parite: 1, vraie: false, code: 'successeur_pour_impair' },
    { texte: 'est toujours pair', expr: 'n^{2}', f: (n) => n * n, parite: 0, vraie: false, code: 'carre' },
    { texte: 'est toujours impair', expr: '2n', f: (n) => 2 * n, parite: 1, vraie: false, code: 'sens_inverse' },
    { texte: 'est toujours pair', expr: '2n + 1', f: (n) => 2 * n + 1, parite: 0, vraie: false, code: 'sens_inverse' },
    { texte: 'est toujours impair', expr: 'n + 2', f: (n) => n + 2, parite: 1, vraie: false, code: 'ajout_pour_double' },
];
POOL.forEach((p) => {
    const toujours = [...Array(21).keys()].every((n) => p.f(n) % 2 === p.parite);
    if (toujours !== p.vraie) throw new Error(`gen_1232 : vérité de ${p.expr} ${p.texte}`);
});

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const tires = shuffle([...POOL]).slice(0, 4);
        if (!tires.some((p) => p.vraie) || !tires.some((p) => !p.vraie)) continue;
        if (new Set(tires.map((p) => p.expr)).size < 3) continue;
        const payload = makeVraiFaux(tires.map((p) => `${tex(p.expr)} ${p.texte} (${tex('n')} entier).`), tires.map((p) => (p.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { affirmations: tires.map(({ expr, texte, vraie }) => ({ expr, texte, vraie })), distracteurs: tires.filter((p) => p.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const tires = [POOL[0], POOL[1], POOL[2], POOL[4]];
    const payload = makeVraiFaux(tires.map((p) => `${tex(p.expr)} ${p.texte} (${tex('n')} entier).`), tires.map((p) => (p.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { affirmations: tires.map(({ expr, texte, vraie }) => ({ expr, texte, vraie })), distracteurs: [{ code: 'ajout_pour_double' }, { code: 'carre' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'deux faits nus : pair = 2n, impair = 2n + 1 ; 2n − 1, 4n, 2n + 2 jamais servis (même vérité que la clé)',
        'pas de fiche sur les QCM (pur rappel, règle 9) ; n = 7 (14 et 15) sacrifié sur la carte numérique',
    ],
    ecriture_qcm_unique: 'quatre écritures : la clé, ajout_pour_double (n + 2), sens_inverse (l\'autre écriture), carre (n²) ou successeur_pour_impair (n + 1)',
    vf_tableau_vf: 'quatre affirmations d\'un pool à vérité fixe, re-vérifiée sur n = 0..20 ; au moins une vraie et une fausse',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    ecriture_qcm_unique: genQcm,
    nombre_texte_trou_saisie: genNombre,
    nombre_saisie: genForme,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'ecriture', label: L_QCM, formatsList: [{ key: 'ecriture_qcm_unique', label: 'QCM' }] },
    { id: 'nombre', label: L_NOMBRE, formatsList: [{ key: 'nombre_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'nombre_ecrire', label: L_FORME, formatsList: [{ key: 'nombre_saisie', label: 'Saisie' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
